import { Plus, MessageSquare, AtSign, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

interface ChatTaskGuideProps {
  onOpenDocumentModal: () => void;
  onPasteQuestion: (question: string) => void;
  onCloseWizard: () => void;
}

export function ChatTaskGuide({ onOpenDocumentModal, onPasteQuestion, onCloseWizard }: ChatTaskGuideProps) {
  const exampleQuestions = [
    'What are the key claims in this patent?',
    'Explain the invention in simple terms',
    'What prior art is cited in the specification?'
  ];

  const handleAddDocuments = () => {
    onCloseWizard();
    setTimeout(() => {
      onOpenDocumentModal();
    }, 100);
  };

  const handlePasteQuestion = (question: string) => {
    onPasteQuestion(question);
    onCloseWizard();
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Let's Start Chatting!</h2>
        <p className="text-gray-600">
          Follow these steps to analyze your documents with AI
        </p>
      </div>

      <div className="space-y-4">
        {/* Step 1: Add Documents */}
        <div className="border-2 border-blue-200 rounded-lg p-5 bg-blue-50">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Add Your Documents</h3>
              <p className="text-sm text-gray-600 mb-3">
                Load patents, publications, or applications to analyze
              </p>
              <Button
                onClick={handleAddDocuments}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <Plus className="w-4 h-4" />
                Add Documents Now
              </Button>
            </div>
          </div>
        </div>

        {/* Step 2: Ask Questions */}
        <div className="border-2 border-purple-200 rounded-lg p-5 bg-purple-50">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Ask Questions</h3>
              <p className="text-sm text-gray-600 mb-3">
                Try one of these example questions:
              </p>
              <div className="space-y-2">
                {exampleQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePasteQuestion(q)}
                    className="w-full text-left text-sm bg-white border border-purple-200 rounded-lg px-3 py-2 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    <MessageSquare className="w-3 h-3 inline mr-2 text-purple-600" />
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Pro Tip */}
        <div className="border-2 border-green-200 rounded-lg p-5 bg-green-50">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Pro Tip: Use @Mentions</h3>
              <p className="text-sm text-gray-600">
                Type <code className="bg-white px-1 rounded">@</code> in your message to mention specific documents
                for more targeted answers.
              </p>
              <div className="mt-2 text-xs text-green-700 bg-white/50 rounded px-2 py-1">
                <AtSign className="w-3 h-3 inline mr-1" />
                Example: "@US10123456 What is claim 1 about?"
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
